/*
 * sidebar-sheet-menu
 */
import { Link } from "@tanstack/react-router"
import { cn } from "@emr/react-ui"
import {
	Button,
	Divider,
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@emr/react-ui"
import { Menu as MenuIcon } from "lucide-react"
import type { ReactNode } from "react"
import { useEffect, useState } from "react"
import { Menu } from "./sidebar-menu"
import { useSidebarToggle } from "./use-sidebar-toggle"
import { SheetSidebarContext } from "./sidebar.context"

type SidebarSheetMenuProps = {
	title: string
	titleHref?: string
	TitleIcon: any
	bottomContent?: ReactNode
	buttonClassName?: string
	children?: ReactNode
}

export function SidebarSheetMenu({
	title,
	titleHref,
	TitleIcon,
	bottomContent,
	buttonClassName,
	children,
}: SidebarSheetMenuProps) {
	const sidebar = useSidebarToggle()
	const TitleContainer = titleHref ? Link : "div"
	const [isMobile, setIsMobile] = useState(false)

	// Sheet sidebar should always start closed on mobile for better UX
	// It can be opened/closed during the session but doesn't persist across refreshes
	const [sheetIsOpen, setSheetIsOpen] = useState(false)

	const handleSheetToggle = (open?: boolean) => {
		setSheetIsOpen((prevOpen) => open ?? !prevOpen)
		if (!open) {
			sidebar.setIsOpen(false)
		}
	}

	useEffect(() => {
		if (sidebar.isOpen) {
			setSheetIsOpen(true)
		}
	}, [sidebar.isOpen])

	useEffect(() => {
		const checkIsMobile = () => {
			setIsMobile(window.innerWidth < 1024)
		}

		// Initial check
		checkIsMobile()

		// Debounced resize handler for better performance
		let timeoutId: NodeJS.Timeout
		const debouncedResize = () => {
			clearTimeout(timeoutId)
			timeoutId = setTimeout(checkIsMobile, 100)
		}

		window.addEventListener("resize", debouncedResize, { passive: true })

		return () => {
			clearTimeout(timeoutId)
			window.removeEventListener("resize", debouncedResize)
		}
	}, [])

	// Don't render Sheet component on large screens to avoid overlay interference
	if (!isMobile) {
		return (
			<Button
				className={cn(["h-8 lg:hidden", buttonClassName])}
				variant={"ghost"}
				size={"icon"}
			>
				<MenuIcon className={"size-6"} />
			</Button>
		)
	}

	return (
		<Sheet open={sheetIsOpen} onOpenChange={handleSheetToggle}>
			<SheetTrigger asChild>
				<Button
					className={cn(["h-8", buttonClassName])}
					variant={"ghost"}
					size={"icon"}
				>
					<MenuIcon className={"size-6"} />
				</Button>
			</SheetTrigger>
			<SheetContent
				className={
					"bg-paper gap-0 text-paper-foreground border-border w-(--sidebar-open-width) flex h-full flex-col px-0 py-2"
				}
				side={"left"}
			>
				<SheetHeader className={"items-center justify-center pb-2"}>
					<SheetTitle>
						<Button
							className={cn([
								"flex items-center justify-center py-0",
								!titleHref ? "text-foreground cursor-default" : "",
							])}
							variant={titleHref ? "link" : "none"}
							asChild
							onClick={() => handleSheetToggle(false)}
						>
							<TitleContainer
								className={"flex items-center gap-2 py-2 h-auto"}
								to={titleHref as any}
							>
								{TitleIcon}
								<h1 className={"whitespace-nowrap text-lg font-bold"}>
									{title}
								</h1>
							</TitleContainer>
						</Button>
					</SheetTitle>
					<SheetDescription className={"hidden"}>Sidebar</SheetDescription>
				</SheetHeader>
				<Divider />
				<SheetSidebarContext.Provider
					value={{
						isInSheet: true,
						closeSheet: () => handleSheetToggle(false),
					}}
				>
					<Menu bottomContent={bottomContent}>{children}</Menu>
				</SheetSidebarContext.Provider>
			</SheetContent>
		</Sheet>
	)
}
